import type {
  Match,
  PickleballGameScore,
  PickleballMatchResult,
  Team,
} from "@/types";

/**
 * A pickleball game ends when:
 *   - a team reaches at least 11 points AND leads by 2, OR
 *   - the score cap of 15 is reached (so 15-14 ends the game even at diff 1).
 */
export function isCompletePickleballGame(g: PickleballGameScore): boolean {
  const hi = Math.max(g.team1Points, g.team2Points);
  const lo = Math.min(g.team1Points, g.team2Points);
  if (hi < 11) return false;
  if (hi >= 15) return true;
  return hi - lo >= 2;
}

export function detectPickleballGameWinner(
  g: PickleballGameScore,
): "team1" | "team2" | null {
  if (!isCompletePickleballGame(g)) return null;
  return g.team1Points > g.team2Points ? "team1" : "team2";
}

/**
 * Best of 5: first team to 3 game wins takes the match.
 */
export function detectPickleballWinner(
  match: Match,
  result: PickleballMatchResult,
): Team["id"] | null {
  if (result?.walkover) return result.winnerId ?? null;
  if (!result?.games?.length) return null;

  let team1Wins = 0;
  let team2Wins = 0;

  for (const g of result.games) {
    const w = detectPickleballGameWinner(g);
    if (w === "team1") team1Wins++;
    else if (w === "team2") team2Wins++;
    if (team1Wins >= 3) return match.team1Id;
    if (team2Wins >= 3) return match.team2Id;
  }

  return null;
}

/**
 * Format a pickleball result string. Drops trailing all-zero (unplayed) games.
 *   "2-1 (11-7, 9-11, 11-5)"  — best-of-5 ended 2-1
 *   "WO"                       — walkover
 */
export function pickleballMatchResultToString(
  result: PickleballMatchResult,
): string {
  if (result.walkover) return "WO";

  const playedGames = result.games.filter(
    (g) => g.team1Points > 0 || g.team2Points > 0,
  );
  if (playedGames.length === 0) return "";

  let team1Wins = 0;
  let team2Wins = 0;
  for (const g of playedGames) {
    const w = detectPickleballGameWinner(g);
    if (w === "team1") team1Wins++;
    else if (w === "team2") team2Wins++;
  }

  const games = playedGames
    .map((g) => `${g.team1Points}-${g.team2Points}`)
    .join(", ");
  return `${team1Wins}-${team2Wins} (${games})`;
}
